import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { useAppSelector } from "../../state/store";
import { VerseType } from "../../types/practiceTypes";
import { shuffle } from "../../util/generalUtil";
import { wordDigitMapping } from "../../util/words";

type Phases = "BEFOREHAND" | "READY" | "MEMORIZING" | "CONTINUE" | "FINISHED";
type WordStatus = "GIVEN" | "C" | "I" | "HIDDEN";
interface MemorizeWord {
  word: string;
  status: WordStatus;
  requireEntire?: boolean;
}
type MemorizeVerseType = VerseType & { isReview: boolean };

export function Memorize() {
  useDocumentTitle("Memorize - Bible Quiz Academy");
  const location = useLocation();
  const material = useAppSelector((state) => state.material.material);
  const settings = useAppSelector((state) => state.settings);
  const chapters =
    location.state?.memorize?.material || settings.selectedChapters;

  const [verseList, setVerseList] = useState<MemorizeVerseType[]>([]);
  const verseListRef = useRef(verseList);
  const [verseDisplay, setVerseDisplay] = useState(verseList[0]);
  const [phase, setPhase] = useState<Phases>("BEFOREHAND");
  const phaseRef = useRef<Phases>(phase);
  const versePhase = useRef(0); // percentage of words that are hidden
  const [words, setWords] = useState<MemorizeWord[]>([]);
  const [wordTyping, setWordTyping] = useState(0);
  const wordTypingRef = useRef(wordTyping);
  const stats = useRef<{ totalVerses: number; versesComplete: number }>({
    totalVerses: 0,
    versesComplete: 0,
  });

  useEffect(() => {
    if (material.length === 0) return;
    let allVerses: MemorizeVerseType[] = [];
    material.forEach((book, index) => {
      book.chapters.forEach((chapter, chapterIndex) => {
        if (chapters && chapters[index] && chapters[index][chapterIndex]) {
          chapter.forEach((verse, verseIndex) => {
            const chapterFromSettings = chapters[index][chapterIndex];
            const chapterSettings =
              typeof chapterFromSettings === "boolean"
                ? Array(chapter.length).fill(chapterFromSettings)
                : chapterFromSettings;
            if (chapterSettings[verseIndex]) {
              allVerses.push({
                book: index,
                chapter: chapterIndex,
                verseNum: verseIndex,
                reference: `${book.bookName} ${chapterIndex + 1}:${
                  verseIndex + 1
                }`,
                isReview: false,
                ...verse,
              });
            }
          });
        }
      });
    });

    stats.current.totalVerses = allVerses.length;

    if (allVerses.length > 1 && settings.memReview > 0) {
      const newList: MemorizeVerseType[] = [];
      // Add for review
      for (let i = 0; i < allVerses.length; i++) {
        newList.push(allVerses[i]);
        if (i + 1 >= settings.memReview) {
          newList.push({
            ...allVerses[i - settings.memReview + 1],
            isReview: true,
          });
        }
      }

      // Some verses at the end
      for (
        let i = Math.max(allVerses.length - settings.memReview + 1, 0);
        i < allVerses.length;
        i++
      ) {
        newList.push({
          ...allVerses[i],
          isReview: true,
        });
      }
      allVerses = newList;
    }
    setVerseList(allVerses);
    verseListRef.current = allVerses;
    setPhase("READY");
    phaseRef.current = "READY";
  }, [material]);
  useEffect(() => {
    if (phase === "MEMORIZING") {
      const el = document.getElementById("mem-text-area");
      if (el) {
        el.focus();
        // @ts-ignore
        el.selectionStart = el.value?.length || 0;
      }
    }
  }, [phase]);

  const characterHandler = (code: string) => {
    if (code === "Space") {
      switch (phaseRef.current) {
        case "READY":
          clickNextVerse();
          break;
        case "CONTINUE":
          clickContinue();
          break;
        default:
          break;
      }
      return;
    }

    if (phaseRef.current === "MEMORIZING") {
      let keyPressed = "";
      let isDigit = false;
      if (code.startsWith("Key"))
        keyPressed = code.substring(3, 4).toLowerCase();
      if (code.startsWith("Digit")) {
        keyPressed = code.substring(5, 6);
        isDigit = true;
      }
      if (code.startsWith("Numpad")) {
        keyPressed = code.substring(6, 7);
        isDigit = true;
      }
      if (/[a-z0-9]/i.test(keyPressed)) {
        setWords((words) => {
          const newWords = [...words];
          const index = newWords.findIndex((w) => w.status === "HIDDEN");
          if (index === -1) return words;

          // Find if the first letter matches
          const thisWord = newWords[index].word.toLowerCase();

          let isMatch = false;

          if (newWords[index].requireEntire) {
            // full word
            isMatch =
              keyPressed ===
              thisWord.substring(
                wordTypingRef.current,
                wordTypingRef.current + 1
              );

            if (isMatch) {
              if (
                thisWord.length === wordTypingRef.current + 1 ||
                !/[a-z0-9]/.test(
                  thisWord.substring(
                    wordTypingRef.current + 1,
                    wordTypingRef.current + 2
                  )
                )
              ) {
                // Word complete, carry on to next word
                wordTypingRef.current = 0;
                setWordTyping(0);
              } else {
                wordTypingRef.current += 1;
                setWordTyping(wordTypingRef.current);
                return words;
              }
            } else {
              wordTypingRef.current = 0;
              setWordTyping(0);
            }
          } else {
            const firstLetter = thisWord.match(/[a-z0-9]/)?.pop();

            isMatch = firstLetter === keyPressed;

            if (!isMatch && isDigit) {
              if (
                wordDigitMapping[thisWord] &&
                String(wordDigitMapping[thisWord]) === keyPressed
              )
                isMatch = true;
            }
          }

          newWords[index] = {
            ...newWords[index],
            status: isMatch ? "C" : "I",
          };
          if (!newWords.some((w) => w.status === "HIDDEN")) {
            const allCorrect = !newWords.some((w) => w.status !== "C");
            if (allCorrect) {
              if (!verseListRef.current[0].isReview)
                stats.current.versesComplete += 1;

              setVerseList((list) => {
                const newVerses = [...list];
                newVerses.splice(0, 1);

                // New phase
                const newPhase: Phases =
                  newVerses.length === 0 ? "FINISHED" : "READY";
                setPhase(newPhase);
                phaseRef.current = newPhase;

                verseListRef.current = newVerses;
                return newVerses;
              });
            } else {
              setPhase("CONTINUE");
              phaseRef.current = "CONTINUE";
            }
          }
          return newWords;
        });
      }
    }
  };
  const upHandler = (e: KeyboardEvent) => {
    characterHandler(e.code);
  };

  useEffect(() => {
    if (!settings.touchscreen) {
      window.addEventListener("keyup", upHandler);
      // Remove event listeners on cleanup
      return () => {
        window.removeEventListener("keyup", upHandler);
      };
    }
  }, []);

  const hideWords = (): MemorizeWord[] => {
    const vPhase = versePhase.current;
    const newWords: MemorizeWord[] = verseListRef.current[0].verse
      .split(" ")
      .map((w) => ({ word: w, status: "GIVEN" }));

    const referenceWords: MemorizeWord[] = [];
    if (
      settings.memRefInput !== "NONE" &&
      ((vPhase > 0 && vPhase <= 15) || vPhase >= 100)
    ) {
      const currentVerse = verseListRef.current[0];
      const refDetails = {
        status: "HIDDEN" as WordStatus,
        requireEntire: true,
      };
      // Add the reference
      if (settings.memRefInput === "FULL") {
        // Book
        const book = material[currentVerse.book].bookName;
        const words = book.split(" ");
        words.forEach((word) => {
          referenceWords.push({
            word,
            ...refDetails,
            requireEntire: false,
          });
        });
      }
      if (settings.memRefInput !== "VERSE") {
        // Chapter
        referenceWords.push({
          word: `${currentVerse.chapter + 1}:`,
          ...refDetails,
        });
      }
      // Verse
      referenceWords.push({
        word: String(currentVerse.verseNum + 1),
        ...refDetails,
      });
    }

    if (vPhase >= 100) {
      // Hide all words
      newWords.forEach((w) => (w.status = "HIDDEN"));
    } else if (vPhase > 0) {
      // Hide some words
      let chunkSize = Math.ceil(vPhase / 25);
      const numberOfChunks = Math.ceil(
        (newWords.length * (vPhase / 100)) / chunkSize
      );
      for (let i = 0; i < numberOfChunks; i++) {
        const possibleIndicies: number[] = [];
        for (let wordIndex = 0; wordIndex < newWords.length; wordIndex++) {
          const w = newWords[wordIndex];
          if (w.status === "GIVEN") {
            let taken = false;
            for (let j = wordIndex - 1; j < wordIndex + chunkSize + 1; j++) {
              if (j - 1 >= newWords.length) {
                taken = true;
                break;
              }
              if (newWords[j] && newWords[j].status !== "GIVEN") taken = true;
            }
            if (!taken) possibleIndicies.push(wordIndex);
          }
        }
        if (possibleIndicies.length > 0) {
          shuffle(possibleIndicies);
          const startIndex = possibleIndicies[0];
          for (let j = startIndex; j < startIndex + chunkSize; j++) {
            newWords[j].status = "HIDDEN";
          }
        } else {
          // Keep trying with smaller chunk sizes
          if (chunkSize > 1) {
            chunkSize--;
            i--;
          }
        }
      }
    }
    return [...referenceWords, ...newWords];
  };

  const clickContinue = () => {
    setPhase("MEMORIZING");
    phaseRef.current = "MEMORIZING";
    setWords((words) => {
      const allCorrect = !words.some((w) => w.status === "I");
      if (allCorrect) versePhase.current += 13 + 50 / words.length;
      if (versePhase.current > 95) versePhase.current = 100;
      return hideWords();
    });
  };
  const clickNextVerse = () => {
    setPhase("CONTINUE");
    phaseRef.current = "CONTINUE";
    const newVerse = verseListRef.current[0];
    setVerseDisplay(newVerse);
    versePhase.current = newVerse.isReview ? 100 : 0;
    setWords(hideWords());
  };

  const currentWordIndex = useMemo(() => {
    return words.findIndex((w) => w.status === "HIDDEN");
  }, [words, wordTyping]);

  return (
    <div
      className="page"
      style={{
        padding: 20,
        minHeight: "calc(100vh - 20px)",
        minWidth: 300,
      }}
    >
      <h1 style={{ position: "fixed", left: -1000, top: -1000 }}>Memorize</h1>
      <div style={{ minHeight: 180, marginTop: 10 }}>
        <div>
          {(phase === "MEMORIZING" ||
            phase === "CONTINUE" ||
            (phase === "READY" && stats.current.versesComplete > 0)) && (
            <>
              <span>{verseDisplay.reference}</span>
              <br />
              <br />
              <span>
                {words.map((w, i) => (
                  <React.Fragment key={i}>
                    <span
                      style={{
                        color: w.status === "I" ? "red" : undefined,
                        textDecoration: w.status === "C" ? "underline" : "",
                      }}
                    >
                      {currentWordIndex === i
                        ? `${words[i].word.substring(0, wordTyping) + "____"}`
                        : w.status === "HIDDEN"
                          ? "_____"
                          : w.word}
                    </span>
                    <span> </span>
                  </React.Fragment>
                ))}
              </span>
            </>
          )}
          {phase === "READY" && stats.current.versesComplete > 0 && (
            <>
              <br />
              <br />
              <span>{`Verse ${
                verseDisplay.isReview ? "Reviewed" : "Memorized"
              }!`}</span>
            </>
          )}
          {phase === "FINISHED" && (
            <>
              <span>Memorization complete!</span>
              <br />
              <br />
              <span>
                You can now review your verses in{" "}
                <Link className="link" to="/versecards">
                  Verse Cards
                </Link>
              </span>
            </>
          )}
        </div>
      </div>
      {phase === "MEMORIZING" &&
        (settings.touchscreen ? (
          <textarea
            id="mem-text-area"
            value="Type the first letter of each blank word. Click here for the digital keyboard."
            style={{
              backgroundColor: "transparent",
              border: "none",
              minHeight: 96,
              color: "inherit",
              width: "100%",
              outline: "none",
              font: "inherit",
              display: "inline-block",
              resize: "none",
              overflow: "hidden",
            }}
            onInput={(e) => {
              const chars = (e.nativeEvent as InputEvent).data || "";
              const key = chars.substring(chars.length - 1, chars.length);
              characterHandler(`Key${key}`);
              e.preventDefault();
              e.stopPropagation();
            }}
          />
        ) : (
          <p>Type the first letter of each blank word.</p>
        ))}
      {phase === "CONTINUE" && (
        <button className="clickable" key="continue" onClick={clickContinue}>
          Continue
        </button>
      )}
      {phase === "READY" && verseList.length > 0 && (
        <button className="clickable" key="next" onClick={clickNextVerse}>
          Next Verse
        </button>
      )}
      <div
        style={{
          visibility:
            phase === "READY" || phase === "FINISHED" ? "visible" : "hidden",
          marginTop: 20,
        }}
      >
        <div>{`Total Verses: ${stats.current.totalVerses}`}</div>
        <div>{`Verses Complete: ${stats.current.versesComplete}`}</div>
      </div>
      <div style={{ marginTop: 20 }}>
        <Link className="secondary-btn" to="/">
          Go Back
        </Link>
      </div>
    </div>
  );
}
